import {Row} from 'react-table';

import {EDragData} from '@ELib/utilities/types';

import {ETableRow, ETableRowDragParams} from './types';

export const handleDragStart = ({
  dragEvent,
  rows,
  selectedItemIds,
  activeRow,
}: ETableRowDragParams & {
  rows: Row<ETableRow<string>>[];
  selectedItemIds: Set<string>;
}): void => {
  const draggedRow = dragEvent.currentTarget;
  if (!activeRow || !draggedRow.draggable) {
    return;
  }

  draggedRow.classList.add('styleDragged', 'styleGhost');
  const rowsToDrag = new Set([
    activeRow.original.id,
    ...selectedItemIds.values(),
  ]);

  if (rowsToDrag.size > 1) {
    const countInfoCell = draggedRow.querySelector(
      'td:not(.selection)'
    ) as HTMLTableCellElement;
    countInfoCell.classList.add('countInfo');
    countInfoCell.dataset.dragCount = `${rowsToDrag.size}`;
  }

  const dragDataList = [...rowsToDrag].reduce<EDragData[]>(
    (accumulatedValue, id) => {
      const row = rows.find((r) => r.original.id === id);
      const dataList = row?.original.options?.dragDataList;
      return dataList ? [...accumulatedValue, ...dataList] : accumulatedValue;
    },
    []
  );

  dragDataList.forEach(({format, data: dragData}) => {
    const currentDragData = dragEvent.dataTransfer.getData(format);
    const separator = currentDragData.length === 0 ? '' : ',';
    const updateDragData = currentDragData.concat(separator, dragData);
    dragEvent.dataTransfer.setData(format, updateDragData);
  });
  dragEvent.dataTransfer.dropEffect = 'move';

  /**
   * When dragging an element, it will be "cloned" and attached to the mouse movement.
   * The cloned element should appear in a slightly different style than the actual element.
   * To achieve that, we apply the style to the actual element on drag which is then inherited by the cloned element.
   * And as soon as the cloned element is dragged, the styling is removed from the actual element but is retained in the cloned element.
   */
  requestAnimationFrame(() => {
    draggedRow.classList.remove('styleGhost');
  });
};

export const handleDragEnd = ({dragEvent}: ETableRowDragParams): void => {
  const draggedRow = dragEvent.target as HTMLTableRowElement;
  draggedRow.classList.remove('styleDragged');
  const countInfoCell = draggedRow.querySelector(
    'td:not(.selection)'
  ) as HTMLTableCellElement;
  countInfoCell.classList.remove('countInfo');
  delete countInfoCell.dataset.dataDragCount;
  countInfoCell.removeAttribute('data-drag-count');
};
